import type { GroupClassSeriesAnalyticsSources } from "@/constants/analytics/Analytics";
import {
  AnalyticsFieldName,
  GroupClassSeriesAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ExploreTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import { useGroupClassDetailsModalDispatch } from "@/store/reducers/modals/groupClassDetailsModal";
import type { GroupClassCardItem } from "@/ui/components/groupClasses/series/GroupClassCard";
import GroupClassCard from "@/ui/components/groupClasses/series/GroupClassCard";
import GroupClassSeriesPreviewCard from "@/ui/components/groupClasses/series/preview/GroupClassSeriesPreviewCard";
import ViewAllCard from "@/ui/composed/cards/ViewAllCard";
import SidewaysHighlightText from "@/ui/core/SidewaysHighlightText";
import { FlatList, HStack } from "@/ui/layout";
import { PreviewCardCount, toPreviewProps } from "@/utils/groupClass.util";
import { joinTestIDs } from "@/utils/string.util";

interface GroupClassSeriesPreviewListProps {
  analyticsSource: GroupClassSeriesAnalyticsSources;
  forWhom?: string;
  // flag on: normalized card view-models; when set, renders GroupClassCard
  groupCardItems?: GroupClassCardItem[];
  groupClasses: GroupClassSeries[];
  isAppUserSupport: boolean;
  isListFetching?: boolean;
  isListLoading?: boolean;
  isRecommended?: boolean;
  onRefresh: () => Promise<void> | void;
  patientId: string;
  shouldShowMostPopularText?: boolean;
  shouldShowViewAllCard?: boolean;
  tabSource: "explore" | "home";
  variant: GroupClassCarouselVariant;
}

interface GroupCardPreviewListProps {
  items: GroupClassCardItem[];
  onRefresh: () => Promise<void> | void;
  onSelect: (args: {
    externalId: string;
    groupSectionId?: string;
    title: string;
  }) => void;
  onViewAllPress: () => void;
  shouldShowViewAllCard?: boolean;
}

// flag-on carousel: caps to a few cards and appends a "View all" card, matching v1
const GroupCardPreviewList = ({
  items,
  onRefresh,
  onSelect,
  onViewAllPress,
  shouldShowViewAllCard,
}: GroupCardPreviewListProps) => {
  const previewItems = items.slice(0, PreviewCardCount);

  return (
    <FlatList
      data={previewItems}
      isHorizontal
      keyExtractor={(item) => item.groupSectionId ?? item.id}
      onRefresh={() => void onRefresh()}
      renderItem={({ index, item }) => (
        <HStack gap={18}>
          <GroupClassCard
            description={item.description}
            groupClassExternalId={item.id}
            iconName={item.iconName}
            isCore={item.isCore}
            isRegistered={item.isRegistered}
            nextDateString={item.nextDateString}
            numberOfOptions={item.numberOfOptions}
            onSelect={(externalId) =>
              onSelect({
                externalId,
                groupSectionId: item.groupSectionId,
                title: item.title,
              })
            }
            recurrenceString={item.recurrenceString}
            size="small"
            title={item.title}
          />
          {shouldShowViewAllCard && index === previewItems.length - 1 && (
            <ViewAllCard
              onPress={onViewAllPress}
              testID={ExploreTestID.GroupClassSeriesCarousel}
            />
          )}
        </HStack>
      )}
      shouldRemoveContentVerticalPadding
      testID={ExploreTestID.GroupClassSeriesCarousel}
    />
  );
};

const GroupClassSeriesPreviewList = ({
  analyticsSource,
  forWhom,
  groupCardItems,
  groupClasses,
  isAppUserSupport,
  isListFetching,
  isListLoading,
  isRecommended,
  onRefresh,
  patientId,
  shouldShowMostPopularText,
  shouldShowViewAllCard,
  tabSource,
  variant,
}: GroupClassSeriesPreviewListProps) => {
  const { track } = useAnalytics();
  const isDisabled = isListFetching || isListLoading;

  const detailsModalPath =
    tabSource === "home"
      ? ModalPath.HomeGroupClassDetails
      : ModalPath.GroupClassDetails;

  const { setGroupClassDetailsData } = useGroupClassDetailsModalDispatch();

  const handleGroupClassCardPress = ({
    externalId,
    groupSectionId,
    title,
  }: {
    externalId: string;
    groupSectionId?: string;
    title: string;
  }) => {
    track(GroupClassSeriesAnalyticsEvent.PreviewCardPressed, {
      [AnalyticsFieldName.Component]: GroupClassSeriesPreviewCard.name,
      [AnalyticsFieldName.GroupClassId]: externalId,
      [AnalyticsFieldName.PatientId]: patientId,
      [AnalyticsFieldName.Source]: analyticsSource,
      title,
    });

    setGroupClassDetailsData({
      forWhom,
      groupClassExternalId: externalId,
      groupSectionId,
      isAppUserSupport,
      isDisabled,
      isRecommended,
      patientId,
      source: analyticsSource,
    });

    router.navigate(detailsModalPath);
  };

  const handleViewAllPress = () => {
    track(GroupClassSeriesAnalyticsEvent.ViewAllGroupsPressed, {
      [AnalyticsFieldName.Component]: ViewAllCard.name,
      [AnalyticsFieldName.PatientId]: patientId,
      [AnalyticsFieldName.Source]: analyticsSource,
    });
    router.navigate({ pathname: ModalPath.GroupClassList });
  };

  const shouldHideEmptyComponent = !isDisabled;

  if (groupCardItems) {
    return (
      <GroupCardPreviewList
        items={groupCardItems}
        onRefresh={onRefresh}
        onSelect={handleGroupClassCardPress}
        onViewAllPress={handleViewAllPress}
        shouldShowViewAllCard={shouldShowViewAllCard}
      />
    );
  }

  return (
    <FlatList
      data={groupClasses}
      // PT-1218: due to the bug with PT-1218, right now, if the list is loading, there is no indication
      // PT-1218: useRefreshControl, isRefreshControlLoading to be enabled pending this bug ticket
      isDataLoading={shouldHideEmptyComponent}
      isHorizontal
      keyExtractor={(item) => item.groupClassExternalId}
      onRefresh={() => void onRefresh()}
      renderItem={({ index, item }) => {
        const {
          groupClassExternalId,
          iconName,
          isUserRegistered,
          recurrenceString,
          timeRange,
          title,
        } = toPreviewProps(item);

        return (
          <HStack gap={18}>
            {shouldShowMostPopularText && index === 0 && (
              <SidewaysHighlightText text="Most popular" />
            )}
            <GroupClassSeriesPreviewCard
              groupClassExternalId={groupClassExternalId}
              iconName={iconName}
              isUserRegistered={isUserRegistered}
              onSelect={(externalId) =>
                handleGroupClassCardPress({ externalId, title })
              }
              recurrenceString={recurrenceString}
              timeRange={timeRange}
              title={title}
            />
            {shouldShowViewAllCard && index === groupClasses.length - 1 && (
              <ViewAllCard
                onPress={handleViewAllPress}
                testID={joinTestIDs(
                  ExploreTestID.GroupClassSeriesCarousel,
                  variant,
                )}
              />
            )}
          </HStack>
        );
      }}
      shouldRemoveContentVerticalPadding
      testID={joinTestIDs(ExploreTestID.GroupClassSeriesCarousel, variant)}
    />
  );
};

export type { GroupClassSeriesPreviewListProps };
export { GroupClassSeriesPreviewList };
